import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { Enum } from "../../../../OW.Remoting/Remoting/DataTypes/Enums";
import { Flags } from "../../../../OW.Remoting/Remoting/DataTypes/Flags";
import { registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IListDataGrid } from "../../IDataGrid";
import { Column } from "../Columns/Column";
import { DataRow } from "./DataRow";
import { IListDataRowElement } from "./IDataRow";
/**
 * Řádek seznamového dataGridu
 */
export class ListDataRow extends DataRow implements IListDataRowElement {
    protected override _dataGrid: IListDataGrid<IListDataRowElement>;

    public override get isValid(): boolean {
        return super.isValid;
    }

    public override set isValid(v: boolean) {
        if (this.checkAndLogDisposedUsage("set isValid")) return;

        super.isValid = v;
    }

    public override get isSelected(): boolean {
        if (this.checkAndLogDisposedUsage("get isSelected")) return false;

        return this._dataGrid.selectedRows.indexOf(this) != -1;
    }

    public override set isSelected(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isSelected")) {
            return;
        }

        const old = this.classList.contains("selectedRecord");
        if (old == value) {
            return;
        }

        this.classList.toggle("selectedRecord", value);

        const index = this._dataGrid.selectedRows.indexOf(this);
        if (value) {
            if (index == -1) {
                this._dataGrid.selectedRows.push(this);
            }
        } else {
            if (index != -1) {
                this._dataGrid.selectedRows.splice(index, 1);
                this._dataGrid.clearIsSelectedAllIfNeeded();
            }
        }

        this.selectionStateChanged.trigger(this, value);
    }

    /**
     * Nastavi záznam visuálně do stavu smazany
     */
    public override get isDeleted(): boolean {
        return super.isDeleted;
    }

    public override set isDeleted(value: boolean) {
        if (this.checkAndLogDisposedUsage("set isDeleted")) return;

        super.isDeleted = value;
    }

    /**
     * Obravení radku
     */
    public override get flag(): Enum | string {
        if (this.checkAndLogDisposedUsage("get flag")) return undefined;

        const flag = this.getAttribute("flag");
        if (flag)
            return Flags.get(flag);
        else
            return undefined;
    }

    public override set flag(v: Enum | string) {
        if (this.checkAndLogDisposedUsage("set flag")) return;

        if (v)
            this.setAttribute("flag", v instanceof Enum ? v.getName() : v);
        else
            this.removeAttribute("flag");
    }

    public removeCellByIndex(index: number): void {
        if (this.checkAndLogDisposedUsage("removeCellByIndex")) return;

        this._dataCells.splice(index, 1).forEach(cell => cell.element?.remove());
    }

    protected override _cleanRowElement(): void {
        this.innerHTML = "";
        this._frozenPart.innerHTML = "";
    }

    protected override _createNoEditableCells(): void {
        if (this.checkAndLogDisposedUsage("_createNoEditableCells")) {
            return;
        }

        this._frozenPart.appendChild(this._createFirstCell());

        for (const cell of this.dataCells) {
            if (cell.column.visible) {
                cell.toEdit(false);
                const newCell = cell.render();
                if (newCell) { //sloupec mohl změnit viditelnost a render vrátil null.
                    if (cell.column.isFrozen) {
                        this._frozenPart.appendChild(newCell);
                    } else {
                        this.appendChild(newCell);
                    }
                }
            }
        }
    }

    public toggleVisibilityCellByAttrName(attrName: string, visible: boolean): void {
        if (this.checkAndLogDisposedUsage("toggleVisibilityCellByAttrName")) return;

        const element = (this.querySelector(`td[data-attrName='${attrName}']`) as HTMLElement);
        if (element) {
            element.style.display = visible ? "" : "none";
            element.classList.toggle("not-visible", !visible);
        }
    }

    /**
     * Změní šířku buňky
     * @param attrName Název atributu, který buňka zobrazuje
     * @param width Nová šířka buňky
     */
    public resizeCellByAttrName(attrName: string, width: number): void {
        if (this.checkAndLogDisposedUsage("resizeCellByAttrName")) return;

        this._resizeCell((this.querySelector(`td[data-attrName='${attrName}']`) as HTMLElement), width);
    }

    /**
     * Změní šířku buňky
     * @param attrName Název atributu, který buňka zobrazuje
     * @param width Nová šířka buňky
     */
    public resizeCellByFullAttrName(attrName: string, width: number): void {
        if (this.checkAndLogDisposedUsage("resizeCellByFullAttrName")) return;

        this._resizeCell((this.querySelector(`td[data-fullattrName='${attrName}']`) as HTMLElement), width);
    }

    /**
     * Změní šířku buňky pro sloupce bez atributu
     * @param index Index, na kterém se buňka nachází
     * @param width Nová šířka buňky
     */
    public resizeCellByIndex(index: number, width: number): void {
        if (this.checkAndLogDisposedUsage("resizeCellByIndex")) return;

        this._resizeCell((this.querySelector(`td:not(.not-visible):nth-child(${index + 2})`) as HTMLElement), width);
    }

    /**
     * Změní šířku požadované buňky
     * @param element Buňka
     * @param width Nová šířka
     */
    private _resizeCell(element: HTMLElement, width: number): void {
        if (this.checkAndLogDisposedUsage("_resizeCell")) return;

        if (element) {
            element.style.width = `${width}px`;
        }
    }

    /**
     * Přesune buňku na jinou pozici
     * @param fullAttrName Název atributu, který buňka zobrazuje
     * @param newPosition Nová pozice, na které má být buňka umístěna
     */
    public changeCellPositionByFullAttrName(fullAttrName: string, newPosition: number): void {
        if (this.checkAndLogDisposedUsage("changeCellPositionByFullAttrName")) return;

        const element = this.querySelector(`td[data-fullAttrName='${fullAttrName}']`) as HTMLElement;

        if (!element) {
            return;
        }

        this._changeDataCells(element, newPosition);
        // index přesouvané buňky z pole pouze viditelných buňek
        const cellToPositionIndex = this._dataCells.filter(cell => cell.column.visible === true).findIndex(cell => cell.column.fullAttrName === fullAttrName);
        this._setCellToPosition(element, cellToPositionIndex, this._getDataCells());
    }

    /**
     * Přesune buňku na jinou pozici u sloupců bez atributu
     * @param index Index, na kterém se buňka nachází
     * @param newPosition Nová pozice, na které má být buňka umístěna
     */
    public changeCellPositionByIndex(index: number, newPosition: number): void {
        if (this.checkAndLogDisposedUsage("changeCellPositionByIndex")) return;

        const targetElements = this._getDataCells();

        let node: HTMLElement = undefined;
        let i = 0;
        for (const element of targetElements) {
            if (i === index) {
                node = element;
                i = 0;
                break;
            } else {
                i++;
            }
        }

        if (!node) {
            return;
        }

        this._changeDataCells(node, newPosition);
        const cellToMovePosition = this._dataGrid.frozenColumnCount + (this._dataCells.length === newPosition ? newPosition : newPosition - 1); // pokud se vkládá na poslední pozici musí se přičíst 1, protože splice neodsune poslední prvek před vkládaný
        this._setCellToPosition(node, cellToMovePosition, targetElements);
    }

    /** Vrátí viditelné datové buňky řádku */
    protected _getDataCells(): HTMLElement[] {
        if (this.checkAndLogDisposedUsage("_getDataCells")) return [];

        return Array.from(this.querySelectorAll("td:not(.not-visible).cellContainer, td:not(.not-visible).EditableCell")) as HTMLElement[];
    }

    /**
     * Aktualizuje interní pole s pořadím buňek podle aktuálního seřazení sloupců
     * @param node Node, obsahující buňky řádku
     * @param newPosition Nová pozice prvku
     */
    private _changeDataCells(node: HTMLElement, newPosition: number): void {
        if (this.checkAndLogDisposedUsage("_changeDataCells")) return;

        const searchAttribute = node.getAttribute("data-fullAttrName");
        // ID je fallback pro sloupce bez atributu
        const removeIndex = this._dataCells.findIndex((item) => item && item.element && (searchAttribute ? item.element.getAttribute("data-fullAttrName") === searchAttribute : item.element.id === node.id));
        const cellToMovePosition = this._dataCells.length == newPosition ? newPosition : newPosition - 1; // pokud se vkládá na poslední pozici musí se přičíst 1, protože splice neodsune poslední prvek před vkládaný
        const cellToMove = this._dataCells.splice(removeIndex, 1); // Nejdřív odebere přemisťovanou buňku ze seznamu buňek
        this._dataCells.splice(cellToMovePosition, 0, ...cellToMove); // A pak ji vloží na požadované místo (index je upraven kvůli předchozímu odebrání)
    }

    /**
     * Vloží buňku na novou pozici
     * @param node Buňka
     * @param newPosition Nová pozice
     * @param targetElements Řádek seznamu
     */
    private _setCellToPosition(node: HTMLElement, newPosition: number, targetElements: HTMLElement[]): void {
        if (this.checkAndLogDisposedUsage("_setCellToPosition")) return;

        let i = 0;
        let append = false;
        const actualPosition = targetElements.findIndex(elem => elem == node); // pozice buňky před přesunutím

        for (const element of targetElements) {
            if (i === newPosition) {
                if (element === node) {
                    return;
                }

                if (actualPosition > newPosition) {
                    element.insertAdjacentElement("beforebegin", node);
                } else {
                    element.insertAdjacentElement("afterend", node);
                }

                append = true;
            }
            i++;
        }

        if (!append) {
            node.parentElement.appendChild(node);
        }
    }

    public override setData(attrName: string, value: IAttrValue): Promise<void> {
        if (this.checkAndLogDisposedUsage("setData")) return undefined;

        return super.setData(attrName, value);
    }

    /**
     * Inicializace datových buněk řádku
     * @param dataRow řádek
     * @param frozenCount počet řádku, ktery jsou fixni (budou vykresleny pouze ty) pokud je číslo menší jak nula budou vykresleny všechny
     * @param columns sloupce k inicializaci... pokud je null, tak se použijí sloupce z dataGridu
     */
    protected override _initCells(dataRow: IRowData, frozenCount?: number, columns?: Column[]): void {
        if (this.checkAndLogDisposedUsage("_initCells")) return;

        if (columns === null || columns === undefined) columns = this._dataGrid.columns; // columns ??= this._dataGrid.columns

        if (frozenCount) {
            let lastFrozenIndex = 0;
            let visibleCount = 0;
            for (const column of columns) {
                lastFrozenIndex++;
                if (column.visible) {
                    visibleCount++;
                }
                if (visibleCount === frozenCount) {
                    break;
                }
            }
            columns = columns.slice(0, lastFrozenIndex);
        }
        super._initCells(dataRow, frozenCount, columns);
    }

    /**
     * Vybere záznam v případě, že lze tuto akci provést (např. není rozeditovaný jiný seznam)
     * @param withFocus Zda má být řádek vybrán s nastavením focusu na seznam. Využívá se při výběru řádku během otevírání menu
     */
    public override async selectWithCheckChanges(withFocus: boolean = true): Promise<void> {
        if (this.checkAndLogDisposedUsage("selectWithCheckChanges")) return;

        return super.selectWithCheckChanges(withFocus);
    }

    /**
     * Kontrola zda se muze zmenit vyber radku. Akce muze byt zablokovana pokud je jiny seznam rozeditovany a neulozeny.
     */
    protected override async _checkIfCanSelectionChange(evt: MouseEvent = null): Promise<boolean> { // bere v potaz i frozen row, který na předkovi neexistuje
        if (this.checkAndLogDisposedUsage("_checkIfCanSelectionChange")) return true;

        if ((this.isSelected) && !this._canChangeSelectedRowMultiSelect(evt)) {
            return true;
        }

        return await this._canChangeSelectedRow();
    }

    protected override _onCellValidityChanged(): void {
        if (this.checkAndLogDisposedUsage("_onCellValidityChanged")) return;

        super._onCellValidityChanged();
    }

    public override connectedCallback(): void {
        if (this.checkAndLogDisposedUsage("connectedCallback")) return;
        super.connectedCallback();
    }
}

registerCustomElement("tsc-listdatarow", ListDataRow);
